import type { OperationExecutionState } from "./types";

export const EXECUTION_ACTIVITY_PHASES = [
  "preparing",
  "awaiting_user",
  "pricing",
  "submitting",
  "confirming",
  "settling",
  "delayed",
  "retrying",
  "active",
  "completed",
  "failed",
  "cancelled",
] as const;

export type ExecutionActivityPhase = (typeof EXECUTION_ACTIVITY_PHASES)[number];

export const EXECUTION_ACTIVITY_EVENT_KINDS = [
  "accepted",
  "awaiting_user",
  "started",
  "pricing_started",
  "price_secured",
  "submitting",
  "submitted",
  "confirmed",
  "settling",
  "settled",
  "delayed",
  "completed",
  "failed",
  "cancelled",
  "progress",
  "market_wait",
  "market_wait_failed",
  "market_retry_required",
  "funding_top_up",
  "deposit_reconciliation",
  "late_settlement",
  "non_user",
  "unknown",
] as const;

export type ExecutionActivityEventKind =
  (typeof EXECUTION_ACTIVITY_EVENT_KINDS)[number];

const normalizeSemanticInput = (value: string | null | undefined): string =>
  value?.trim().toLowerCase() ?? "";

const PHASE_BY_RAW_PHASE = new Map<string, ExecutionActivityPhase>([
  ["accepted", "preparing"],
  ["quoting", "pricing"],
  ["pricing", "pricing"],
  ["waiting_quote", "pricing"],
  ["materialized", "submitting"],
  ["submitting", "submitting"],
  ["submitted", "submitting"],
  ["awaiting_transaction", "submitting"],
  ["confirming", "confirming"],
  ["confirmed", "confirming"],
  ["waiting_settlement", "settling"],
  ["indexing", "settling"],
  ["waiting_indexing", "settling"],
  ["reconciliation", "settling"],
  ["settled", "settling"],
  ["settlement_delayed", "delayed"],
  ["running", "active"],
  ["waiting_result", "active"],
  ["waiting_funding_settlement", "active"],
  ["waiting_market_window", "active"],
  ["in_transit", "active"],
]);

export const classifyExecutionActivityPhase = (params: {
  readonly currentPhase: string | null | undefined;
  readonly state: OperationExecutionState;
}): ExecutionActivityPhase => {
  if (params.state === "completed") return "completed";
  if (params.state === "failed") return "failed";
  if (params.state === "cancelled") return "cancelled";
  if (params.state === "awaiting_user") return "awaiting_user";

  const phase = normalizeSemanticInput(params.currentPhase);
  const semanticPhase = PHASE_BY_RAW_PHASE.get(phase);
  if (semanticPhase) return semanticPhase;
  if (params.state === "accepted") return "preparing";
  if (params.state === "retrying") return "retrying";
  return "active";
};

const EVENT_KIND_BY_RAW_KIND = new Map<string, ExecutionActivityEventKind>([
  ["deposit.chain_interpretation_rejected", "deposit_reconciliation"],
  ["deposit.intent_created", "awaiting_user"],
  ["deposit.transaction_submitted", "submitted"],
  ["deposit.chain_observed", "settling"],
  ["deposit.indexer_wait", "settling"],
  ["deposit.delayed", "delayed"],
  ["deposit.settled", "settled"],
  ["deposit.failed", "failed"],
  ["deposit.chain_observation_retracted", "failed"],
  ["execution.accepted", "accepted"],
  ["execution.started", "started"],
  ["execution.action_started", "started"],
  ["execution.action_market_hold", "market_wait"],
  ["execution.awaiting_quote", "pricing_started"],
  ["execution.quote_received", "price_secured"],
  ["execution.route_materialized", "price_secured"],
  ["execution.action_submitting", "submitting"],
  // Internal evidence that a Solana action recorded signed bytes, which the
  // engine consults before treating a failed leg as cleanly isolated.
  ["execution.action_submission_prepared", "non_user"],
  ["execution.action_submitted", "submitted"],
  ["execution.submission_lease_acquired", "submitting"],
  ["execution.action_confirmed", "confirmed"],
  // A fill is a confirmed receipt that also proves what the account received.
  // It stays in the existing "confirmed" class so older clients parsing the
  // event-kind enum keep working; the amount rides on `operation.fill`.
  ["execution.action_filled", "confirmed"],
  // Internal sealed-block re-read of a preconfirmed fill; its user-visible
  // effect is `operation.fill` being upgraded or removed.
  ["execution.action_fill_checked", "non_user"],
  ["execution.funding_settlement_wait", "settling"],
  ["execution.indexer_settlement_wait", "settling"],
  ["execution.bridge_status", "settling"],
  ["execution.bridge_destination_expected", "settling"],
  ["execution.action_settled", "settled"],
  ["execution.funding_settlement_passed", "settled"],
  ["execution.float_recovery_settled", "settled"],
  ["execution.late_settlement_proceeds_in_vault", "late_settlement"],
  ["execution.action_delayed", "delayed"],
  ["execution.delayed", "delayed"],
  ["execution.funding_settlement_delayed", "delayed"],
  ["execution.indexer_settlement_delayed", "delayed"],
  ["execution.action_completed", "completed"],
  ["execution.completed", "completed"],
  ["execution.action_failed", "failed"],
  ["execution.action_failed_recorded", "failed"],
  ["execution.failed", "failed"],
  ["execution.funding_shortfall", "failed"],
  ["execution.market_hold_rejected", "market_wait_failed"],
  ["execution.market_hold_manual_retry_required", "market_retry_required"],
  ["execution.cancelled", "cancelled"],
  ["execution.action_progress", "progress"],
  ["execution.action_progressed", "progress"],
  ["execution.awaiting_action_result", "progress"],
  ["execution.progressed", "progress"],
  ["execution.funding_top_up_attempted", "funding_top_up"],
  ["execution.callback_delivered", "non_user"],
  ["execution.callback_failed", "non_user"],
  ["execution.callback_delivery_claimed", "non_user"],
  ["execution.cancellation_workflow_termination_failed", "non_user"],
]);

export const classifyExecutionActivityEventKind = (
  kind: string,
): ExecutionActivityEventKind => EVENT_KIND_BY_RAW_KIND.get(kind) ?? "unknown";

export const classifyExecutionActivityEventPhase = (params: {
  readonly eventKind: ExecutionActivityEventKind;
  readonly status: string | null | undefined;
}): ExecutionActivityPhase => {
  if (params.status === "failed" || params.status === "blocked")
    return "failed";
  switch (params.eventKind) {
    case "accepted":
      return "preparing";
    case "awaiting_user":
      return "awaiting_user";
    case "pricing_started":
    case "price_secured":
      return "pricing";
    case "submitting":
    case "submitted":
      return "submitting";
    case "confirmed":
      return "confirming";
    case "settling":
    case "settled":
      return "settling";
    case "delayed":
      return "delayed";
    case "completed":
      return "completed";
    case "failed":
      return "failed";
    case "cancelled":
      return "cancelled";
    case "market_wait":
      return "active";
    case "market_wait_failed":
    case "market_retry_required":
    case "deposit_reconciliation":
      return "failed";
    case "funding_top_up":
      return "active";
    case "late_settlement":
      return "settling";
    case "non_user":
      return "active";
    case "started":
    case "progress":
    case "unknown":
      return params.status === "completed" ? "completed" : "active";
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN =
  /^[a-z][a-z0-9_]{1,127}$/u;

const readFailureCodeCandidate = (value: unknown): string | null =>
  typeof value === "string" &&
  EXECUTION_ACTIVITY_FAILURE_CODE_PATTERN.test(value)
    ? value
    : null;

export const readExecutionActivityFailureCode = (
  data: unknown,
): string | null => {
  if (!isRecord(data)) return null;
  const failure = isRecord(data.failure) ? data.failure : null;
  return (
    readFailureCodeCandidate(failure?.code) ??
    readFailureCodeCandidate(data.failureCode) ??
    readFailureCodeCandidate(data.reason) ??
    (data.status === "REFUNDED" ? "bridge_refunded" : null) ??
    null
  );
};

export const isReturnedExecutionFailureCode = (
  failureCode: string | null | undefined,
): boolean =>
  failureCode === "bridge_refunded" ||
  failureCode === "bridge_returned" ||
  failureCode === "funds_returned";
